import {
  Component,
  HostListener,
  Inject,
  Input,
  OnInit, PLATFORM_ID,
  SimpleChanges
} from '@angular/core';
import {ElevationStats, EventItem, Lookups, Race, RaceRoute, Waypoint, WaypointType} from "@model";
import {environment} from "@environment";
import {RaceRouteService} from "@services";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent} from "@components";
import {getLanguageValue} from "@utility";
import {DOCUMENT, isPlatformBrowser} from "@angular/common";

@Component({
  selector: 'app-race-map-tab',
  templateUrl: './race-map-tab.component.html',
  styleUrl: './race-map-tab.component.scss'
})
export class RaceMapTabComponent extends AbstractComponent implements OnInit {
  @Input() eventItem: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() raceRoute: RaceRoute | undefined;
  @Input() waypointList: Waypoint[] = [];
  @Input({required: true}) languages: Lookups[] = [];

  activeTabId: number = 1;
  gpxWaypoints: Waypoint[] = [];
  waypointsStats: Map<number, ElevationStats> = new Map<number, ElevationStats>();
  lat: number = environment.headquartersLat;
  lng: number = environment.headquartersLng;
  currentWindowWidth: number = 0;
  controlPointsList: Waypoint[] = [];
  gpx?: any;

  constructor(@Inject(DOCUMENT) private document: Document, @Inject(PLATFORM_ID) private platformId: Object,
    public translocoService: TranslocoService, private raceRouteService: RaceRouteService) {
    super();
    if (isPlatformBrowser(this.platformId)) {
      this.currentWindowWidth = window.innerWidth;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.raceRoute?.content && changes['raceRoute']?.currentValue !== changes['raceRoute']?.previousValue) {
      if (isPlatformBrowser(this.platformId)) {
        import("../../../../components/leaflet-maps/gpx-layer").then(({GPX}) => {
          this.gpx = new GPX(this.raceRoute!.content, {}, () => {
          });
          this.generateRaceProfileData();
        });
      }
    } else {
      this.gpx = undefined;
      this.gpxWaypoints = []
      this.waypointsStats.clear();
    }
  }


  ngOnInit(): void {
    this.filterControlPoints();
    if (isPlatformBrowser(this.platformId)) {
      this.downloadGpx();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.currentWindowWidth = event.target.innerWidth;
  }

  private generateRaceProfileData(): void {
    if (this.gpx) {
      this.waypointList.sort((a, b) => (a.distanceFromStart ?? 0) - (b.distanceFromStart ?? 0));
      this.gpxWaypoints = []
      const statsIndexes: number[] = [];
      let pointInd: number = 0;
      for (let routePointInd = 0; routePointInd < this.gpx._coords.length; routePointInd++) {
        const lat = this.gpx._coords[routePointInd].lat.toFixed(4);
        const lng = this.gpx._coords[routePointInd].lng.toFixed(4);
        for (let checkPointIndex = pointInd; checkPointIndex < this.waypointList.length; checkPointIndex++) {
          const checkpoint: Waypoint = this.waypointList[checkPointIndex];
          if (checkpoint.latitude?.toFixed(4) === lat && checkpoint.longitude?.toFixed(4) === lng) {
            this.gpxWaypoints.push(checkpoint);
            statsIndexes.push(routePointInd);
            pointInd = checkPointIndex + 1;
            break;
          }
        }
        if (statsIndexes.length === this.waypointList.length) break;
      }
      this.gpxWaypoints.forEach((waypoint: Waypoint, index: number) => {
        const curr = statsIndexes[index];
        const prev = index > 0 ? statsIndexes[index - 1] : undefined

        if (waypoint.id) {
          this.waypointsStats.set(waypoint.id, {
            gainFromStart: this.gpx!.getElevationPointsMap()[curr]?.gain,
            lossFromStart: this.gpx!.getElevationPointsMap()[curr]?.loss,
            gainFromPrevious: this.gpx!.getElevationPointsMap()[curr]?.gain - (prev ? this.gpx!.getElevationPointsMap()[prev]?.gain : 0),
            lossFromPrevious: this.gpx!.getElevationPointsMap()[curr]?.loss - (prev ? this.gpx!.getElevationPointsMap()[prev]?.loss : 0)
          })
        }
      })
    }
  }


  downloadGPXFile(): void {
    if (this.raceRoute?.content && this.race?.raceSettings?.enableGPXDownload) {
      const filename: string = `${(getLanguageValue(this.race?.name, this.race?.nameEng, this.race?.nameIta, this.race?.nameSlo, this.race?.nameSr, this.race?.nameBs, this.translocoService.getActiveLang()) ?? "").split(/\s+/).join("_")}_route.gpx`;
      const decodedData: string = atob(this.raceRoute.content.split(",")[1]);

      const blob: Blob = new Blob([decodedData], {type: 'application/gpx+xml'});
      const link: HTMLAnchorElement = this.document.createElement('a');

      link.href = window.URL.createObjectURL(blob);
      link.download = filename;

      this.document.body.appendChild(link);
      link.click();
      this.document.body.removeChild(link);
    }
  }

  filterControlPoints(): void {
    if ((this.waypointList.length ?? 0) > 0) {
      this.controlPointsList = this.waypointList.filter((item: Waypoint): boolean => item.waypointTypeId === WaypointType.CONTROL_WAYPOINT);
    }
  }

  private downloadGpx() {
    if (this.raceRoute && this.raceRoute.route && this.raceRoute.route !== "") {
      this.raceRouteService.downloadGpx(this.raceRoute.route, (base64data: string) => {
        this.raceRoute!.content = base64data;
      });
    }
  }
}
